import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Send, Heart, Phone, Sun,
  HelpCircle, Smile, ChevronLeft, RefreshCw,
  Users, Lock
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../store/AuthContext';
import { supabase } from '../lib/supabase';

interface Message {
  id: number;
  user_id: string;
  sender_id: string;
  sender_type: 'senior' | 'family';
  member_id: string | null;
  message: string;
  message_type: 'quick' | 'custom';
  read_at: string | null;
  created_at: string;
}

interface FamilyMember {
  id: string;
  display_name: string | null;
  email: string;
  status: string;
}

export function CareChat() {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [messages, setMessages] = useState<Message[]>([]);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<string | 'all'>('all');
  const [showCompose, setShowCompose] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const quickMessages = [
    { key: 'ccQuickWell',     icon: <Smile size={18} />,   text: t('ccQuickWell')     || "I'm doing well 😊" },
    { key: 'ccQuickCall',     icon: <Phone size={18} />,   text: t('ccQuickCall')     || 'Can someone call me? 📞' },
    { key: 'ccQuickGreat',    icon: <Sun size={18} />,     text: t('ccQuickGreat')    || 'Having a great day! ☀️' },
    { key: 'ccQuickThinking', icon: <Heart size={18} />,   text: t('ccQuickThinking') || 'Thinking of you ❤️' },
    { key: 'ccQuickHelp',     icon: <HelpCircle size={18} />, text: t('ccQuickHelp') || 'Need some help 🙏' },
  ];

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadMembers = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('care_circle_members')
      .select('id, display_name, email, status')
      .eq('user_id', user.id)
      .eq('status', 'active');
    if (data) setMembers(data);
  }, [user?.id]);

  const loadMessages = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('care_messages')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(100);
    if (data) setMessages(data);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    Promise.all([loadMembers(), loadMessages()]).finally(() => setLoading(false));
  }, [loadMembers, loadMessages]);

  // ── Realtime ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('care_messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'care_messages',
        filter: `user_id=eq.${user.id}`,
      }, payload => {
        setMessages(prev => [...prev, payload.new as Message]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // ── Scroll to bottom ────────────────────────────────────────────────────────

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ────────────────────────────────────────────────────────────

  const sendMessage = async (text: string, type: 'quick' | 'custom' = 'custom') => {
    if (!user?.id || !text.trim()) return;
    setSending(true);
    setError('');
    try {
      const { error: insertError } = await supabase
        .from('care_messages')
        .insert({
          user_id: user.id,
          sender_id: user.id,
          sender_type: 'senior',
          member_id: selectedMember === 'all' ? null : selectedMember,
          message: text.trim(),
          message_type: type,
        });
      if (insertError) throw insertError;

      // Notify family via email
      const targets = selectedMember === 'all'
        ? members
        : members.filter(m => m.id === selectedMember);

      if (targets.length > 0) {
        await fetch('/api/chat/notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            senderEmail: user.email,
            message: text.trim(),
            recipients: targets.map(m => ({ email: m.email, name: m.display_name || m.email })),
          }),
        });
      }

      setCustomMessage('');
      setShowCompose(false);
      setSuccess(t('ccMessageSent') || 'Message sent!');
      setTimeout(() => setSuccess(''), 3000);
      await loadMessages();
    } catch (e) {
      setError(t('ccSendError') || 'Could not send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // ── Format time ─────────────────────────────────────────────────────────────

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return t('ccToday') || 'Today';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // ── Guest guard ──────────────────────────────────────────────────────────────

  if (!user || user.id === 'guest') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center gap-4">
        <Lock size={48} className="text-rose-400" />
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-200">
          {t('fsSignInDescription')}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw size={32} className="animate-spin text-rose-400" />
      </div>
    );
  }

  // ── Group messages by date ───────────────────────────────────────────────────

  const grouped: { date: string; msgs: Message[] }[] = [];
  messages.forEach(msg => {
    const date = formatDate(msg.created_at);
    const last = grouped[grouped.length - 1];
    if (last && last.date === date) last.msgs.push(msg);
    else grouped.push({ date, msgs: [msg] });
  });

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 px-4 pb-32">

      {/* Header */}
      <div className="flex items-center gap-3 pt-4">
        <MessageCircle size={28} className="text-rose-500" />
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('ccChatWithFamily') || 'Chat with Family'}
        </h1>
      </div>

      {/* No family members */}
      {members.length === 0 && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-3xl p-5 flex flex-col items-center gap-3 text-center">
          <Users size={32} className="text-rose-400" />
          <p className="text-gray-600 dark:text-gray-300 font-semibold">
            {t('ccNoFamily') || 'No family members connected yet'}
          </p>
          <p className="text-sm text-gray-400">
            {t('ccNoFamilyHint') || 'Add family members in the Family Sharing section first'}
          </p>
        </div>
      )}

      {/* Member selector */}
      {members.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setSelectedMember('all')}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              selectedMember === 'all'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            {t('ccEveryone') || 'Everyone'}
          </button>
          {members.map(m => (
            <button
              key={m.id}
              onClick={() => setSelectedMember(m.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                selectedMember === m.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              }`}
            >
              {m.display_name || m.email.split('@')[0]}
            </button>
          ))}
        </div>
      )}

      {/* Feedback */}
      <AnimatePresence>
        {success && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-2xl p-3 text-green-600 dark:text-green-400 text-sm text-center">
            {success}
          </motion.div>
        )}
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-2xl p-3 text-red-600 dark:text-red-400 text-sm text-center">
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message feed */}
      <div className="flex flex-col gap-1 min-h-[200px]">
        {grouped.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <MessageCircle size={40} className="text-gray-200 dark:text-gray-600" />
            <p className="text-gray-400 dark:text-gray-500">
              {t('ccNoMessages') || 'No messages yet. Send your first message!'}
            </p>
          </div>
        )}

        {grouped.map(group => (
          <div key={group.date} className="flex flex-col gap-2">
            {/* Date divider */}
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-100 dark:bg-gray-700" />
              <span className="text-xs text-gray-400 font-medium">{group.date}</span>
              <div className="flex-1 h-px bg-gray-100 dark:bg-gray-700" />
            </div>

            {group.msgs.map(msg => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.sender_type === 'senior' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                  msg.sender_type === 'senior'
                    ? 'bg-rose-500 text-white rounded-br-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm rounded-bl-sm'
                }`}>
                  {msg.sender_type === 'family' && (
                    <p className="text-xs font-semibold text-rose-400 mb-1">
                      {members.find(m => m.id === msg.member_id)?.display_name || t('ccFamilyMember') || 'Family'}
                    </p>
                  )}
                  <p className="text-base leading-snug">{msg.message}</p>
                  <p className={`text-xs mt-1 ${msg.sender_type === 'senior' ? 'text-rose-200' : 'text-gray-400'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Quick messages */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-sm flex flex-col gap-3">
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
          {t('ccQuickSend') || 'Quick messages'}
        </p>
        <div className="flex flex-col gap-2">
          {quickMessages.map(qm => (
            <motion.button
              key={qm.key}
              whileTap={{ scale: 0.97 }}
              onClick={() => sendMessage(qm.text, 'quick')}
              disabled={sending || members.length === 0}
              className="w-full flex items-center gap-3 px-4 py-3 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 disabled:opacity-50 rounded-2xl text-left transition-colors"
            >
              <span className="text-rose-400">{qm.icon}</span>
              <span className="text-gray-700 dark:text-gray-200 text-base">{qm.text}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Custom message */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-sm flex flex-col gap-3">
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
          {t('ccYourMessage') || 'Your own message'}
        </p>
        <textarea
          value={customMessage}
          onChange={e => setCustomMessage(e.target.value)}
          placeholder={t('ccTypeSomething') || 'Type something...'}
          rows={3}
          className="w-full px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-base resize-none focus:outline-none focus:ring-2 focus:ring-rose-300"
        />
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => sendMessage(customMessage, 'custom')}
          disabled={sending || !customMessage.trim() || members.length === 0}
          className="w-full py-3 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
        >
          {sending
            ? <RefreshCw size={18} className="animate-spin" />
            : <Send size={18} />
          }
          {sending ? t('ccSending') || 'Sending...' : t('ccSendMessage') || 'Send Message'}
        </motion.button>
      </div>

      {/* Privacy note */}
      <p className="text-xs text-gray-400 text-center px-4 pb-2">
        {t('ccPrivacyNote') || 'Messages are private and only visible to your care circle.'}
      </p>
    </div>
  );
}
